import React, {
    MouseEvent,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react'
import { Link } from 'react-router-dom'
import {
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    Fade,
    IconButton,
} from '@mui/material'
import {
    UsersIcon,
    TenantModulesIcon,
    EditIcon,
    MoreVertIcon,
    ProjectsIcon,
} from '../components'
import { translate } from '../i18n'
import { Tenant } from '../model'
import { AppContext } from '../context'
import TenantModulesForm from './TenantModulesForm'

interface TenantMenuProps {
    tenant: Tenant
}

export default function TenantMenu({ tenant }: TenantMenuProps) {
    const context = useContext(AppContext)

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [popup, setPopup] = useState(false)

    const allowedUpdate = useMemo(
        () => context.isGranted('TenantsUpdate'),
        [context]
    )
    const allowedUsers = useMemo(
        () => context.isGranted('UsersRead'),
        [context]
    )
    const allowedProjects = useMemo(
        () => context.isGrantedAny(['ProjectsRead', 'TProjectsRead']),
        [context]
    )
    const allowedModules = useMemo(
        () => context.isGranted('TenantModulesRead'),
        [context]
    )

    const onClickedOptions = useCallback((event: MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
    }, [])

    const onClickedClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const onShowModules = useCallback(() => {
        onClickedClose()
        setPopup(true)
    }, [onClickedClose])

    return (
        <>
            <IconButton
                aria-label="options"
                color="default"
                size="small"
                onClick={onClickedOptions}
            >
                <MoreVertIcon />
            </IconButton>
            {anchorEl && (
                <Menu
                    id="tenants-item"
                    anchorEl={anchorEl}
                    keepMounted
                    onClose={onClickedClose}
                    open
                    TransitionComponent={Fade}
                >
                    <MenuItem
                        component={Link}
                        to={`/tenants/${tenant.id}/edit`}
                        onClick={onClickedClose}
                        disabled={!allowedUpdate}
                        dense
                    >
                        <ListItemIcon>
                            <EditIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <Typography variant="inherit">
                            {translate('buttons.edit')}
                        </Typography>
                    </MenuItem>
                    <MenuItem
                        component={Link}
                        to={`/tenants/${tenant.id}/users`}
                        onClick={onClickedClose}
                        dense
                        disabled={!allowedUsers}
                    >
                        <ListItemIcon>
                            <UsersIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <Typography variant="inherit">
                            {translate('users.title')}
                        </Typography>
                    </MenuItem>
                    <MenuItem
                        component={Link}
                        to={`/tenants/${tenant.id}/projects`}
                        onClick={onClickedClose}
                        dense
                        disabled={!allowedProjects}
                    >
                        <ListItemIcon>
                            <ProjectsIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <Typography variant="inherit">
                            {translate('projects.title')}
                        </Typography>
                    </MenuItem>
                    <MenuItem
                        onClick={onShowModules}
                        dense
                        disabled={!allowedModules}
                    >
                        <ListItemIcon>
                            <TenantModulesIcon
                                fontSize="small"
                                color="primary"
                            />
                        </ListItemIcon>
                        <Typography variant="inherit">
                            {translate('tenant_modules.title')}
                        </Typography>
                    </MenuItem>
                </Menu>
            )}

            {popup && (
                <TenantModulesForm
                    tenantId={tenant.id}
                    onClose={() => setPopup(false)}
                />
            )}
        </>
    )
}
